import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../apiClient'

type ChangelogBase = {
  cursor: number
  limit: number
  createdAtStart?: string
  createdAtEnd?: string
  triggeredByUserIds?: string | string[]
  orderBy?: string
  sortDescending?: boolean
}

function normalizeChangelogParams(params: ChangelogBase): ChangelogBase {
  if (!params.triggeredByUserIds) {
    return params
  }

  const triggeredByUserIds = (
    Array.isArray(params.triggeredByUserIds) ? params.triggeredByUserIds : [params.triggeredByUserIds]
  )
    .flatMap((value) => value.split(','))
    .filter(Boolean)

  return { ...params, triggeredByUserIds }
}

type ChangelogProgramRequest = ChangelogBase & {
  programId?: string
}

type ChangelogMSRequest = ChangelogBase & {
  userId?: string
}

type MinimalUserResponse = {
  id: string
  name: string
  avatarUrl: string
}

type ProgramChangelogResponse = {
  total: number
  changelogs: ProgramChangelogItemResponse[]
  users: MinimalUserResponse[]
}

type ProgramChangedPropertyResponse = {
  attribute: string
  humanReadableAttributeName: string
  category: string
}

type ProgramChangelogItemResponse = {
  id: string
  createdAt: string
  type?: string
  function: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rawChanges: Record<string, any>
  categories: string[]
  changedProperties: ProgramChangedPropertyResponse[]
  triggeredByUserId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>
}

type TriggerUsersResponse = {
  users: MinimalUserResponse[]
}

export type UserChangelogResponse = {
  changelogs: ProgramChangelogItemResponse[]
  users: MinimalUserResponse[]
  total: number
}

export const CHANGE_LOG_BY_PROGRAM_ID_KEY = 'change-log-by-program-id'
export const CHANGE_LOG_BY_MS_ID_KEY = 'change-log-by-ms-id'
export const CHANGE_LOG_USERS_BY_PROGRAM_ID_KEY = 'change-log-users-by-program-id'
export const CHANGE_LOG_USERS_BY_MS_ID_KEY = 'change-log-users-by-ms-id'

export function useGetProgramChangeLogList({ programId, ...params }: ChangelogProgramRequest) {
  const normalizedParams = normalizeChangelogParams(params)

  return useQuery({
    queryKey: [CHANGE_LOG_BY_PROGRAM_ID_KEY, programId, normalizedParams],
    queryFn: async () => {
      const { data } = await apiClient.get<ProgramChangelogResponse>(
        `/int/v3/changelogs/program/${programId}`,
        {
          params: normalizedParams,
        }
      )
      return data
    },
    refetchOnWindowFocus: false,
    enabled:
      Boolean(programId) && (normalizedParams.createdAtStart ? Boolean(normalizedParams.createdAtEnd) : true),
  })
}

export function useGetProgramUsers(programId?: string) {
  return useQuery({
    queryKey: [CHANGE_LOG_USERS_BY_PROGRAM_ID_KEY, programId],
    queryFn: async () => {
      const { data } = await apiClient.get<TriggerUsersResponse>(
        `/int/v3/changelogs/program/${programId}/users`
      )
      return data
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(programId),
  })
}

export function useGetMSChangeLogList({ userId, ...params }: ChangelogMSRequest) {
  const normalizedParams = normalizeChangelogParams(params)

  return useQuery({
    queryKey: [CHANGE_LOG_BY_MS_ID_KEY, userId, normalizedParams],
    queryFn: async () => {
      const { data } = await apiClient.get<UserChangelogResponse>(`/int/v3/changelogs/user/${userId}`, {
        params: normalizedParams,
      })
      return data
    },
    refetchOnWindowFocus: false,
    enabled:
      Boolean(userId) && (normalizedParams.createdAtStart ? Boolean(normalizedParams.createdAtEnd) : true),
  })
}

export function useGetMSUsers(userId?: string) {
  return useQuery({
    queryKey: [CHANGE_LOG_USERS_BY_MS_ID_KEY, userId],
    queryFn: async () => {
      const { data } = await apiClient.get<TriggerUsersResponse>(`/int/v3/changelogs/user/${userId}/users`)

      return data
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(userId),
  })
}
